<template>
  <div class="agenda-page">
    <section class="content">
      <!-- View Selector using PrimeVue SelectButton -->
      <div class="view-selector">
        <SelectButton 
          v-model="currentView" 
          :options="viewOptions" 
          optionLabel="label" 
          optionValue="value"
          @change="onViewChange"
          :allowEmpty="false"
        >
          <template #option="slotProps">
            <i :class="slotProps.option.icon"></i>
            <span>{{ slotProps.option.label }}</span>
          </template>
        </SelectButton>
      </div>

      <div class="calendar-container">
        <Box variant="primary" :padding="false">
          <FullCalendar
            ref="fullCalendar"
            :options="calendarOptions"
          />

          <!-- Loading overlay -->
          <div v-if="isLoading" class="loading-overlay">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
        </Box>
      </div>
    </section>

    <!-- WizardServicosModal is centralized in ModalManager.vue - opened via eventBus -->

    <!-- AgendaAddCompromissoWizard is centralized in ModalManager.vue - opened via eventBus -->

    <!-- AgendaBloqueioModal is centralized in ModalManager.vue - opened via eventBus -->

    <!-- GestaoCompromissoModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import FullCalendar from '@fullcalendar/vue3';
import type { CalendarOptions, DateSelectArg, EventSourceFuncArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import SelectButton from '../../ui/SelectButton.vue';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { eventBus } from '../../eventBus';
import { useAgenda } from '../../composables/useAgenda';
import { useBloqueioAgenda } from '../../composables/useBloqueioAgenda';
import type { Agenda_AlterarCompromissoInput } from '../../contracts/Agenda_AlterarCompromissoInput';
import '../../contracts/Agenda_AlterarCompromissoInput';
import { permissionsService } from '../../services/permissionsService';
import { log } from '../../utils/DebugLogger';
// Box is globally registered
// WizardServicosModal is centralized in ModalManager.vue - opened via eventBus
import AgendaAddCompromissoWizard from './AgendaAddCompromissoWizard.vue';
// AgendaBloqueioModal is centralized in ModalManager.vue - opened via eventBus

// GestaoCompromissoModal is centralized in ModalManager.vue

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    __agendaCalendarApi?: any;
  }
}

interface Compromisso {
  compromisso_id?: number | string;
  compromisso_data_hora_inicio?: string;
  compromisso_data_hora_fim?: string;
  compromisso_total?: string | number;
  compromisso_valor_total?: string | number;
  compromisso_status?: string;
  /** the stored status; compromisso_status is its translated label */
  compromisso_status_original?: string;
  compromisso_observacao?: string;
  compromisso_bairro?: string;
  compromisso_franquia_id?: number | string;
  compromisso_forma_pagamento?: number | string;
  compromisso_origem?: string;
  backgroundColor?: string;
  cliente_nome?: string;
  cliente?: string;
  localidade_nome?: string;
  localidade?: string;
  orcamento_bairro?: string;
  avatar_operador?: string;
  nome_operador?: string;
  operador_nome?: string;
  franquia_nome?: string;
  start?: string;
  end?: string;
  title?: string;
  [key: string]: unknown;
}

interface Bloqueio {
  bloqueio_agenda_id?: number | string;
  bloqueio_agenda_titulo?: string;
  bloqueio_agenda_operador_user_id?: number | string;
  bloqueio_operador_nome?: string;
  backgroundColor?: string;
  avatar_operador?: string;
  start?: string;
  end?: string;
  [key: string]: unknown;
}

interface ViewOption {
  value: string;
  label: string;
  icon: string;
}

interface FetchInfo {
  startStr: string;
  endStr: string;
  start: Date;
  end: Date;
}

interface AgendaEvent {
  id: string;
  title: string;
  start: string | undefined;
  end: string | undefined;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  editable?: boolean;
  durationEditable?: boolean;
  extendedProps: {
    tipo: string;
    compromisso_tipo: number;
    compromisso?: Compromisso;
    bloqueio?: Bloqueio;
    tooltip: string;
  };
  classNames?: string[];
}

const { t, n, locale } = useI18n();
const { buscarCompromissos, alterarCompromisso } = useAgenda();
const { saveBloqueioAgenda } = useBloqueioAgenda();

// Reactive state
const fullCalendar = ref<InstanceType<typeof FullCalendar> | null>(null);
const isLoading = ref<boolean>(false);
const compromissos = ref<Compromisso[]>([]);
const bloqueios = ref<Bloqueio[]>([]);
const lastFetchedEvents = ref<AgendaEvent[]>([]);
let activeFetchPromise: Promise<AgendaEvent[]> | null = null;
let refreshDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const calendarHeight = ref<number>(700); // Default height, will be calculated dynamically

// Every setTimeout scheduled by this page goes through trackedTimeout so that
// onUnmounted can cancel it. A timer that outlives the component fires against a
// destroyed FullCalendar API (the scroll chains capture `calendarApi` in their
// closure), a detached element (the tippy deferral) or a foreign `.fc` node
// (the debug listeners) — the source of the random AgendaPage test failures.
// Some of them are scheduled from inside a nextTick continuation, which can land
// after the unmount — hence the isUnmounted guard, so nothing gets queued into an
// already-drained set.
const pendingTimers = new Set<ReturnType<typeof setTimeout>>();
let isUnmounted = false;
function trackedTimeout(callback: () => void, delay: number): ReturnType<typeof setTimeout> | null {
  if (isUnmounted) return null;
  const timerId = setTimeout(() => {
    pendingTimers.delete(timerId);
    callback();
  }, delay);
  pendingTimers.add(timerId);
  return timerId;
}

// Wizard state - now managed by WizardServicosModal via eventBus
// showWizard and selectedCompromissoId removed - modal is centralized in ModalManager.vue

// Bloqueio modal state removed - modal is centralized in ModalManager.vue

// Get user group
const userGroup = computed(() => permissionsService.getUserGroup());

// FullCalendar locale mapping
const getFullCalendarLocale = (): string => {
  const currentLocale = locale.value || 'pt-BR';

  // Direct mapping to FullCalendar locale codes
  const localeMap: Record<string, string> = {
    // English variants
    'en-US': 'en',
    'en-GB': 'en-gb',
    'en-AU': 'en-au',
    'en-CA': 'en',
    'en-NZ': 'en-nz',
    'en-ZA': 'en',

    // Portuguese variants
    'pt-BR': 'pt-br',
    'pt-PT': 'pt',
    'pt-AO': 'pt',
    'pt-MZ': 'pt',

    // Spanish variants
    'es-ES': 'es',
    'es-MX': 'es',
    'es-AR': 'es',
    'es-CO': 'es',
    'es-GT': 'es',
    'es-CL': 'es',

    // French variants
    'fr-FR': 'fr',
    'fr-CA': 'fr-ca',
    'fr-BE': 'fr',
    'fr-CH': 'fr-ch',

    // German variants
    'de-DE': 'de',
    'de-AT': 'de-at',
    'de-CH': 'de',
    'de-LU': 'de',

    // Dutch variants
    'nl-NL': 'nl',
    'nl-BE': 'nl-be',

    // Italian variants
    'it-IT': 'it',
    'it-CH': 'it',

    // Other languages
    'ru-RU': 'ru',
    'ru-BY': 'ru',
    'ja-JP': 'ja',
    'zh-CN': 'zh-cn',
    'zh-TW': 'zh-tw',
    'zh-HK': 'zh-tw',
    'sv-SE': 'sv',
    'da-DK': 'da',
    'fi-FI': 'fi',
    'nb-NO': 'nb',
    'pl-PL': 'pl',
    'cs-CZ': 'cs',
    'hu-HU': 'hu',
    'ro-RO': 'ro',
    'uk-UA': 'uk',
    'el-GR': 'el',
    'tr-TR': 'tr',
    'ar-SA': 'ar',
    'ar-AE': 'ar',
    'ar-EG': 'ar',
    'ar-MA': 'ar',
    'hi-IN': 'hi',
    'ko-KR': 'ko',
    'id-ID': 'id',
    'vi-VN': 'vi'
  };

  // Try exact match first
  if (localeMap[currentLocale]) {
    return localeMap[currentLocale];
  }

  // Fallback: extract language code (e.g., 'es-ES' → 'es')
  const langCode = currentLocale.split('-')[0].toLowerCase();
  return langCode || 'en';
};

// Mobile detection
const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

// Current view tracking
const currentView = ref('dayGridMonth');

// View options for selector
const viewOptions = computed((): ViewOption[] => [
  {
    value: 'dayGridMonth',
    label: t('Mês'),
    icon: 'pi pi-calendar'
  },
  {
    value: 'timeGrid3Days',
    label: t('3 Dias'),
    icon: 'pi pi-calendar-minus'
  },
  {
    value: 'timeGridWeek',
    label: t('Semana'),
    icon: 'pi pi-calendar-minus'
  },
  {
    value: 'timeGridDay',
    label: t('Dia'),
    icon: 'pi pi-calendar-plus'
  }
]);

// Handle view change from SelectButton
function onViewChange(event: { value: string }): void {
  log('AgendaPage', 'onViewChange', 'View changed', { view: event.value });
  const calendarApi = fullCalendar.value?.getApi();
  if (calendarApi) {
    calendarApi.changeView(event.value);
  }
}

// Calculate available height for calendar
function calculateCalendarHeight(): number {
  // Get the calendar container element
  const calendarContainer = document.querySelector('.calendar-container');
  if (!calendarContainer) {
    log('AgendaPage', 'calculateCalendarHeight', 'Calendar container not found, using default');
    return 700;
  }

  // Get the top position of the calendar container relative to viewport
  const containerRect = calendarContainer.getBoundingClientRect();
  const topOffset = containerRect.top;

  // Calculate available height: viewport height - top offset - bottom padding
  const bottomPadding = 20; // Space at the bottom
  const availableHeight = window.innerHeight - topOffset - bottomPadding;

  // Set minimum height to avoid too small calendars
  const minHeight = isMobile.value ? 400 : 500;
  const calculatedHeight = Math.max(availableHeight, minHeight);

  log('AgendaPage', 'calculateCalendarHeight', 'Calculated calendar height', {
    viewportHeight: window.innerHeight,
    topOffset: topOffset,
    bottomPadding: bottomPadding,
    availableHeight: availableHeight,
    minHeight: minHeight,
    finalHeight: calculatedHeight
  });

  return calculatedHeight;
}

// Update mobile detection
function updateMobileDetection(): void {
  isMobile.value = window.innerWidth < 768;
  log('AgendaPage', 'updateMobileDetection', 'Mobile detection updated', {
    isMobile: isMobile.value,
    width: window.innerWidth
  });
}

// Calendar options with mobile-first approach
const calendarOptions = computed((): CalendarOptions => {
  const mobile = isMobile.value;

  return {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    // Mobile-first view selection
    initialView: mobile ? 'timeGrid3Days' : 'timeGridWeek',
    locale: getFullCalendarLocale(),
    
    // Mobile-optimized header toolbar (removed view buttons - now external)
    headerToolbar: mobile ? {
      left: 'prev,next',
      center: 'title',
      right: 'today'
    } : {
      left: 'prev,next today',
      center: 'title',
      right: ''
    },
    
    buttonText: {
      today: t('Hoje'),
      month: t('Mês'),
      week: t('Semana'),
      day: t('Dia')
    },
    
    // Mobile-optimized views (using only free dayGrid views)
    views: {
      // Mobile-optimized week view
      timeGrid3Days: {
        type: 'timeGrid',
        duration: { days: 3 },
        eventMinHeight: mobile ? 25 : 18
      },
    },
    
    // Mobile-optimized display settings
    allDaySlot: false, // Hide all-day row
    height: calendarHeight.value, // Dynamic height based on available viewport space
    nowIndicator: true,
    navLinks: !mobile, // Disable nav links on mobile (use touch instead)
    
    // Mobile-optimized interaction settings
    editable: true,
    droppable: false,
    eventDurationEditable: true,
    selectable: true,
    selectMirror: true,
    selectMinDistance: mobile ? 5 : 0, // Slightly more distance needed on mobile
    selectLongPressDelay: mobile ? 150 : 250, // Faster response on mobile
    
    // Mobile touch optimization
    longPressDelay: mobile ? 150 : 250,
    eventLongPressDelay: mobile ? 150 : 250,
    dragRevertDuration: mobile ? 200 : 500, // Faster revert on mobile
    
    // Mobile-optimized event display
    dayMaxEvents: mobile ? 2 : true,
    weekends: true,
    eventDisplay: 'block',
    eventMinHeight: mobile ? 30 : 20,
    eventShortHeight: mobile ? 25 : 15,
    
    // Time range configuration
    // slotMinTime: '06:00:00', // Start calendar at 8am
    // slotMaxTime: '23:00:00', // End calendar at 8pm (adjust as needed)
    scrollTime: '08:00:00', // Initial scroll position at 8am

    // Event handlers — cast needed: async function matches EventSourceFunc callback overload at runtime
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    events: fetchEvents as any,
    eventClick: handleEventClick,
    select: handleDateSelect,
    eventDidMount: handleEventDidMount,
    eventDrop: handleEventDrop,
    eventResize: handleEventResize,
    datesSet: handleDatesSet,
    loading: handleLoading
    // windowResize is handled via window.addEventListener in onMounted
  };
});

// Fetch events from API
async function fetchEvents(fetchInfo: EventSourceFuncArg, successCallback: (events: AgendaEvent[]) => void, failureCallback: (error: unknown) => void): Promise<void> {
  log('AgendaPage', 'fetchEvents', 'Fetching events', {
    start: fetchInfo.startStr,
    end: fetchInfo.endStr
  });

  // If a fetch is already in flight, wait for it instead of returning stale/empty cache
  if (activeFetchPromise) {
    log('AgendaPage', 'fetchEvents', 'Fetch in flight, waiting for active request');
    try {
      const events = await activeFetchPromise;
      successCallback(events);
    } catch (error) {
      failureCallback(error);
    }
    return;
  }

  activeFetchPromise = (async () => {
    const response = await buscarCompromissos(
      userGroup.value === 'Operador' ? 'Operador' : '',
      {
        start: fetchInfo.startStr,
        end: fetchInfo.endStr
      }
    ) as { dados?: { compromissos?: Compromisso[]; bloqueios?: Bloqueio[] } };

    const compromissosData: Compromisso[] = response.dados?.compromissos || [];
    const bloqueiosData: Bloqueio[] = response.dados?.bloqueios || [];

    compromissos.value = compromissosData;
    bloqueios.value = bloqueiosData;

    const compromissoEvents = compromissosData.map((compromisso: Compromisso) => formatCompromissoEvent(compromisso));
    const bloqueioEvents = bloqueiosData.map((bloqueio: Bloqueio) => formatBloqueioEvent(bloqueio));

    const allEvents = [...compromissoEvents, ...bloqueioEvents];

    log('AgendaPage', 'fetchEvents', 'Events loaded', {
      compromissos: compromissoEvents.length,
      bloqueios: bloqueioEvents.length
    });

    lastFetchedEvents.value = allEvents;
    return allEvents;
  })();

  try {
    const allEvents = await activeFetchPromise;
    successCallback(allEvents);
  } catch (error) {
    log('AgendaPage', 'fetchEvents', 'Error fetching events', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar compromissos'));
    failureCallback(error);
  } finally {
    activeFetchPromise = null;
  }
}

// A finished or cancelled compromisso cannot be moved (decision 2,
// docs/plans/doutor-flow-test-coverage.md §0.1). The backend refuses too.
const LOCKED_COMPROMISSO_STATUSES = ['Realizado', 'Cancelado'];
function isCompromissoLocked(compromisso: Compromisso): boolean {
  return LOCKED_COMPROMISSO_STATUSES.includes(String(compromisso.compromisso_status_original ?? ''));
}

// Format compromisso as FullCalendar event
function formatCompromissoEvent(compromisso: Compromisso): AgendaEvent {
  // Use operator's background color (from backend)
  let backgroundColor = compromisso.backgroundColor || '#3788d8';
  let borderColor = 'white';
  const locked = isCompromissoLocked(compromisso);

  const event = {
    id: `compromisso_${compromisso.compromisso_id}`,
    title: '', // Will be populated with HTML in eventDidMount
    start: compromisso.compromisso_data_hora_inicio || compromisso.start,
    end: compromisso.compromisso_data_hora_fim || compromisso.end,
    backgroundColor: backgroundColor,
    borderColor: borderColor,
    textColor: '#ffffff',
    editable: !locked,
    durationEditable: !locked,
    extendedProps: {
      tipo: 'compromisso',
      compromisso_tipo: 1,
      compromisso: compromisso,
      tooltip: buildCompromissoTooltip(compromisso)
    }
  };

  return event;
}

// Format bloqueio as FullCalendar event
function formatBloqueioEvent(bloqueio: Bloqueio): AgendaEvent {
  const tooltipHTML = `
    <div class="tooltip-container">
      <div class="tooltip-header tooltip-status-default">
        <i class="pi pi-ban tooltip-icon"></i>
        <span><strong>${t('Bloqueio de agenda')}</strong></span>
      </div>
      <div class="tooltip-divider"></div>
      <div class="tooltip-body">
        <div class="tooltip-row">
          <span class="tooltip-label">${t('Título')}:</span>
          <span class="tooltip-value">${bloqueio.bloqueio_agenda_titulo || '-'}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">${t('Operador')}:</span>
          <span class="tooltip-value">${bloqueio.bloqueio_operador_nome || '-'}</span>
        </div>
      </div>
    </div>
  `;

  return {
    id: `bloqueio_${bloqueio.bloqueio_agenda_id}`,
    title: '', // Will be populated with HTML in eventDidMount
    start: bloqueio.start,
    end: bloqueio.end,
    backgroundColor: bloqueio.backgroundColor || '#999999',
    borderColor: 'white',
    textColor: '#ffffff',
    editable: true,
    extendedProps: {
      tipo: 'bloqueio',
      compromisso_tipo: 2,
      bloqueio: bloqueio,
      tooltip: tooltipHTML
    },
    classNames: ['event-bloqueio']
  };
}

// Build compromisso display HTML
function buildCompromissoDisplay(compromisso: Compromisso, viewType = 'timeGrid'): string {
  const total = parseFloat(String(compromisso.compromisso_total || compromisso.compromisso_valor_total || 0));
  const cliente = compromisso.cliente_nome || compromisso.cliente || '';
  const localidade = compromisso.localidade_nome || compromisso.localidade || '';
  const bairro = compromisso.compromisso_bairro || compromisso.orcamento_bairro || compromisso.title || '';

  // Month view - simple text
  if (viewType === 'dayGridMonth') {
    return `${cliente} - ${n(total, 'currency')}`;
  }

  // Week/Day view - rich HTML
  let html = '';

  // Operator avatar
  if (compromisso.avatar_operador) {
    html += `<div class="img_cards_container pull-right"><img class="img_cards" src="${compromisso.avatar_operador}" /></div>`;
  }

  // Container with status styling
  let containerStyle = '';
  if (compromisso.compromisso_status === t('Realizado')) {
    containerStyle = 'background-color: rgba(255,255,255,0.3); padding-top: 5px; padding-bottom: 5px';
  } else if (compromisso.compromisso_status === 'Cancelado') {
    containerStyle = 'background-color: rgba(0,0,0,0.3); padding-top: 5px; padding-bottom: 5px; opacity: 0.5';
  }

  html += `<div class="compromissos" style="${containerStyle}">`;

  // Status icons
  if (compromisso.compromisso_status === t('Realizado')) {
    html += '<i class="pi pi-check-circle" style="margin-right: 5px"></i>';
  } else if (compromisso.compromisso_status === 'Cancelado') {
    html += '<i class="pi pi-ban" style="margin-right: 5px"></i>';
  }

  // Client name
  html += `<span class="text-capitalize">${cliente}</span><br/>`;

  // Total
  html += `<span>${t('Total')}: ${n(total, 'currency')}</span><br/>`;

  // Location and neighborhood
  html += `<span><span class="text-capitalize">${localidade} - ${bairro}</span></span>`;

  html += '</div>';

  return html;
}

// Build compromisso title for display (fallback)
function buildCompromissoTitle(compromisso: Compromisso): string {
  const total = parseFloat(String(compromisso.compromisso_total || compromisso.compromisso_valor_total || 0));
  const cliente = compromisso.cliente_nome || compromisso.cliente || '';
  return `${cliente} - ${n(total, 'currency')}`;
}

// Build compromisso tooltip (HTML)
function buildCompromissoTooltip(compromisso: Compromisso): string {
  const total = parseFloat(String(compromisso.compromisso_total || compromisso.compromisso_valor_total || 0));

  let statusClass = 'tooltip-status-default';
  let iconeStatusCompromisso = '';

  if (compromisso.compromisso_status === t('Realizado')) {
    statusClass = 'tooltip-status-success';
    iconeStatusCompromisso = '<i class="pi pi-check tooltip-icon"></i>';
  }

  let observacoes = '';
  if (compromisso.compromisso_observacao && compromisso.compromisso_observacao !== '') {
    observacoes = `
      <div class="tooltip-row">
        <span class="tooltip-label">${t('Obs')}:</span>
        <span class="tooltip-value">${compromisso.compromisso_observacao}</span>
      </div>
    `;
  }

  const tooltip = `
    <div class="tooltip-container">
      <div class="tooltip-header ${statusClass}">
        ${iconeStatusCompromisso}
        <span><strong>Status:</strong> ${compromisso.compromisso_status || '-'}</span>
      </div>
      <div class="tooltip-divider"></div>
      <div class="tooltip-body">
        <div class="tooltip-row">
          <span class="tooltip-label">${t('Operador')}:</span>
          <span class="tooltip-value">${compromisso.nome_operador || compromisso.operador_nome || '-'}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">${t('Franquia')}:</span>
          <span class="tooltip-value">${compromisso.franquia_nome || '-'}</span>
        </div>
        <div class="tooltip-row">
          <span class="tooltip-label">${t('Total')}:</span>
          <span class="tooltip-value">${n(total, 'currency')}</span>
        </div>
        ${observacoes}
      </div>
    </div>
  `;

  return tooltip;
}

// Build bloqueio display HTML
function buildBloqueioDisplay(bloqueio: Bloqueio, viewType = 'timeGrid'): string {
  const titulo = bloqueio.bloqueio_agenda_titulo || t('Bloqueio');
  const operador = bloqueio.bloqueio_operador_nome || '-';

  // Month view - simple text
  if (viewType === 'dayGridMonth') {
    return `${t('Bloqueio')}: ${titulo}`;
  }

  // Week/Day view - rich HTML
  let html = '';

  // Operator avatar
  if (bloqueio.avatar_operador) {
    html += `<div class="img_cards_container pull-right"><img class="img_cards" src="${bloqueio.avatar_operador}" /></div>`;
  }

  html += `<div class="compromissos" style="padding-top: 5px; padding-bottom: 5px">`;

  // Block icon
  html += '<i class="pi pi-ban" style="margin-right: 5px"></i>';

  // Title
  html += `<strong>${titulo}</strong><br>`;

  // Operator
  html += `<span>${operador}</span>`;

  html += '</div>';

  return html;
}

// Handle event click
function handleEventClick(clickInfo: { event: { extendedProps: Record<string, unknown> } }): void {
  const { extendedProps } = clickInfo.event;

  log('AgendaPage', 'handleEventClick', 'Event clicked', extendedProps);

  if (extendedProps.tipo === 'compromisso') {
    const compromisso = extendedProps.compromisso as Compromisso;
    // Open GestaoCompromissoModal via eventBus (modal is in ModalManager.vue)
    log('AgendaPage', 'handleEventClick', 'Opening gestao compromisso modal via eventBus', compromisso.compromisso_id);
    eventBus.emit('gestao-compromisso.open', {
      compromissoId: Number(compromisso.compromisso_id)
    });
  } else if (extendedProps.tipo === 'bloqueio') {
    const bloqueio = extendedProps.bloqueio as Bloqueio;
    // TODO: Open bloqueio modal
    eventBus.emit('bloqueio-agenda.open', {
      bloqueioId: bloqueio.bloqueio_agenda_id
    });
  }
}

// Handle date click (single tap - better for mobile)
function handleDateClick(dateClickInfo: { dateStr: string; allDay: boolean; date: Date; jsEvent?: Event; view: { type: string }; resource?: unknown }): void {
  log('AgendaPage', 'handleDateClick', 'Date clicked', {
    date: dateClickInfo.dateStr,
    allDay: dateClickInfo.allDay,
    jsEvent: dateClickInfo.jsEvent?.type,
    view: dateClickInfo.view?.type,
    resource: dateClickInfo.resource
  });

  // Only handle time grid clicks (not day/month view)
  if (dateClickInfo.view.type.includes('timeGrid')) {
    const start = new Date(dateClickInfo.date);
    // Default to 1 hour appointment
    const end = new Date(start.getTime() + 60 * 60 * 1000);

    log('AgendaPage', 'handleDateClick', 'Opening wizard from date click', {
      start: start.toISOString(),
      end: end.toISOString()
    });

    // Open add compromisso wizard
    eventBus.emit('agenda-add-compromisso.open', {
      start: start,
      end: end
    });
  }
}

// Handle date select (time slot click/drag)
function handleDateSelect(selectInfo: DateSelectArg): void {
  log('AgendaPage', 'handleDateSelect', 'Date selected - START', {
    start: selectInfo.startStr,
    end: selectInfo.endStr,
    allDay: selectInfo.allDay,
    jsEvent: (selectInfo.jsEvent as MouseEvent | undefined)?.type,
    view: selectInfo.view?.type
  });

  // Calculate duration and ensure minimum 1 hour
  const start = new Date(selectInfo.start);
  let end = new Date(selectInfo.end);
  const durationMs = end.getTime() - start.getTime();
  const oneHourMs = 60 * 60 * 1000;

  // If duration is less than 1 hour, set it to 1 hour
  if (durationMs < oneHourMs) {
    end = new Date(start.getTime() + oneHourMs);
  }

  log('AgendaPage', 'handleDateSelect', 'Calculated dates', {
    start: start.toISOString(),
    end: end.toISOString(),
    duration: durationMs
  });

  log('AgendaPage', 'handleDateSelect', 'Emitting agenda-add-compromisso.open event');

  // Open add compromisso wizard
  eventBus.emit('agenda-add-compromisso.open', {
    start: start,
    end: end
  });

  log('AgendaPage', 'handleDateSelect', 'Event emitted successfully');

  // Small delay to allow event to propagate
  trackedTimeout(() => {
    log('AgendaPage', 'handleDateSelect', 'Checking if wizard opened');
  }, 100);

  // Clear selection
  const calendarApi = fullCalendar.value?.getApi();
  if (calendarApi) {
    calendarApi.unselect();
  }
  
  log('AgendaPage', 'handleDateSelect', 'Date selected - END');
}

// Handle event rendering
function handleEventDidMount(info: { event: { extendedProps: Record<string, unknown> }; view: { type: string }; el: HTMLElement }): void {
  const { extendedProps } = info.event;

  // Inject custom HTML for compromissos
  if (extendedProps.tipo === 'compromisso' && extendedProps.compromisso) {
    const viewType = info.view.type;
    const htmlContent = buildCompromissoDisplay(extendedProps.compromisso as Compromisso, viewType);

    // Find the title element and inject HTML
    const titleElement = info.el.querySelector('.fc-event-title') ||
                         info.el.querySelector('.fc-event-title-container') ||
                         info.el.querySelector('.fc-content');

    if (titleElement) {
      titleElement.innerHTML = htmlContent;
    }
  }

  // Inject custom HTML for bloqueios
  if (extendedProps.tipo === 'bloqueio' && extendedProps.bloqueio) {
    const viewType = info.view.type;
    const htmlContent = buildBloqueioDisplay(extendedProps.bloqueio as Bloqueio, viewType);

    // Find the title element and inject HTML
    const titleElement = info.el.querySelector('.fc-event-title') ||
                         info.el.querySelector('.fc-event-title-container') ||
                         info.el.querySelector('.fc-content');

    if (titleElement) {
      titleElement.innerHTML = htmlContent;
    }

    // Add custom styling for bloqueios
    info.el.style.border = '3px dashed white';
    info.el.style.padding = '5px';
  }

  // Add Tippy tooltip with HTML content (defer to avoid swallowing first click)
  if (extendedProps.tooltip) {
    trackedTimeout(() => {
      tippy(info.el, {
        content: extendedProps.tooltip as string,
        allowHTML: true,
        theme: 'agenda',
        placement: 'top',
        arrow: true,
        interactive: true,
        appendTo: () => document.body,
        maxWidth: 450,
        zIndex: 100000,
        delay: [200, 0],
        animation: 'scale'
      });
    }, 0);
  }
}

// Handle dates change (view change)
function handleDatesSet(dateInfo: { view: { type: string }; startStr: string; endStr: string }): void {
  log('AgendaPage', 'handleDatesSet', 'View changed', {
    view: dateInfo.view.type,
    start: dateInfo.startStr,
    end: dateInfo.endStr,
    isMobile: isMobile.value
  });

  // Update current view tracking (and ensure SelectButton reflects initial mobile default)
  currentView.value = dateInfo.view.type;
  if (isMobile.value && dateInfo.view.type === 'timeGridWeek') {
    const api = fullCalendar.value?.getApi();
    if (api) {
      api.changeView('timeGrid3Days');
      currentView.value = 'timeGrid3Days';
      log('AgendaPage', 'handleDatesSet', 'Forced mobile default to timeGrid3Days');
    }
  }

  // Auto-adjust view for mobile if needed
  if (isMobile.value && dateInfo.view.type === 'timeGridWeek') {
    // On mobile, week view might be too cramped, suggest day view
    log('AgendaPage', 'handleDatesSet', 'Mobile detected with week view - consider day view for better UX');
  }

  // Scroll to 8am after view changes (needed for scrollTime to work consistently)
  const calendarApi = fullCalendar.value?.getApi();
  if (calendarApi && dateInfo.view.type.includes('timeGrid')) {
    // Multiple scroll attempts with increasing delays
    nextTick(() => {
      trackedTimeout(() => {
        calendarApi.scrollToTime({ hours: 8 });
        log('AgendaPage', 'handleDatesSet', 'Scrolled to 8:00 AM after view change (attempt 1)');

        trackedTimeout(() => {
          calendarApi.scrollToTime({ hours: 8 });
          log('AgendaPage', 'handleDatesSet', 'Scrolled to 8:00 AM after view change (attempt 2 - final)');
        }, 200);
      }, 100);
    });
  }
}

// Handle loading state
function handleLoading(loading: boolean): void {
  isLoading.value = loading;
}

// Handle window resize for mobile detection
function handleWindowResize(): void {
  updateMobileDetection();

  // Recalculate and update calendar height
  const newHeight = calculateCalendarHeight();
  calendarHeight.value = newHeight;

  // Update calendar size after resize
  const calendarApi = fullCalendar.value?.getApi();
  if (calendarApi) {
    calendarApi.setOption('height', newHeight);
    calendarApi.updateSize();
    log('AgendaPage', 'handleWindowResize', 'Calendar height updated', { newHeight });
  }
}

// Format date for backend
function formatDateTimeForBackend(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

interface DropResizeInfo {
  event: {
    extendedProps: Record<string, unknown>;
    start: Date | null;
    end: Date | null;
    startStr?: string;
    endStr?: string;
  };
  revert: () => void;
}

// Handle event drop (drag to new time/date)
async function handleEventDrop(dropInfo: DropResizeInfo): Promise<void> {
  const { event } = dropInfo;
  const tipo = event.extendedProps?.tipo as string | undefined;
  const compromisso = event.extendedProps?.compromisso as Compromisso | undefined;
  const bloqueio = event.extendedProps?.bloqueio as Bloqueio | undefined;

  if (tipo === 'compromisso' && compromisso) {
    log('AgendaPage', 'handleEventDrop', 'Compromisso dropped - FULL DATA', {
      compromisso: compromisso,
      compromisso_franquia_id: compromisso.compromisso_franquia_id,
      compromisso_forma_pagamento: compromisso.compromisso_forma_pagamento
    });

    if (isCompromissoLocked(compromisso)) {
      log('AgendaPage', 'handleEventDrop', 'Locked status, reverting', compromisso.compromisso_status_original);
      dropInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Compromisso realizado ou cancelado não pode ser reagendado')
      });
      return;
    }

    // Validate critical fields before sending update
    if (!compromisso.compromisso_franquia_id || !compromisso.compromisso_forma_pagamento) {
      log('AgendaPage', 'handleEventDrop', 'CRITICAL: Missing required fields, reverting', {
        compromisso_franquia_id: compromisso.compromisso_franquia_id,
        compromisso_forma_pagamento: compromisso.compromisso_forma_pagamento
      });
      dropInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Erro: dados incompletos. Por favor, recarregue a página.')
      });
      return;
    }

    try {
      await updateCompromisso(compromisso, event.start, event.end);
    } catch (error) {
      log('AgendaPage', 'handleEventDrop', 'Error updating, reverting', error);
      dropInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Erro ao atualizar compromisso')
      });
    }
  } else if (tipo === 'bloqueio' && bloqueio) {
    log('AgendaPage', 'handleEventDrop', 'Bloqueio dropped', {
      id: bloqueio.bloqueio_agenda_id,
      start: event.startStr,
      end: event.endStr
    });

    try {
      await updateBloqueio(bloqueio, event.start, event.end);
    } catch (error) {
      log('AgendaPage', 'handleEventDrop', 'Error updating, reverting', error);
      dropInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Erro ao atualizar bloqueio')
      });
    }
  }
}

// Handle event resize (change duration)
async function handleEventResize(resizeInfo: DropResizeInfo): Promise<void> {
  const { event } = resizeInfo;
  const tipo = event.extendedProps?.tipo as string | undefined;
  const compromisso = event.extendedProps?.compromisso as Compromisso | undefined;
  const bloqueio = event.extendedProps?.bloqueio as Bloqueio | undefined;

  if (tipo === 'compromisso' && compromisso) {
    log('AgendaPage', 'handleEventResize', 'Compromisso resized - FULL DATA', {
      compromisso: compromisso,
      compromisso_franquia_id: compromisso.compromisso_franquia_id,
      compromisso_forma_pagamento: compromisso.compromisso_forma_pagamento
    });

    if (isCompromissoLocked(compromisso)) {
      log('AgendaPage', 'handleEventResize', 'Locked status, reverting', compromisso.compromisso_status_original);
      resizeInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Compromisso realizado ou cancelado não pode ser reagendado')
      });
      return;
    }

    // Validate critical fields before sending update
    if (!compromisso.compromisso_franquia_id || !compromisso.compromisso_forma_pagamento) {
      log('AgendaPage', 'handleEventResize', 'CRITICAL: Missing required fields, reverting', {
        compromisso_franquia_id: compromisso.compromisso_franquia_id,
        compromisso_forma_pagamento: compromisso.compromisso_forma_pagamento
      });
      resizeInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Erro: dados incompletos. Por favor, recarregue a página.')
      });
      return;
    }

    try {
      await updateCompromisso(compromisso, event.start, event.end);
    } catch (error) {
      log('AgendaPage', 'handleEventResize', 'Error updating, reverting', error);
      resizeInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Erro ao atualizar compromisso')
      });
    }
  } else if (tipo === 'bloqueio' && bloqueio) {
    log('AgendaPage', 'handleEventResize', 'Bloqueio resized', {
      id: bloqueio.bloqueio_agenda_id,
      start: event.startStr,
      end: event.endStr
    });

    try {
      await updateBloqueio(bloqueio, event.start, event.end);
    } catch (error) {
      log('AgendaPage', 'handleEventResize', 'Error updating, reverting', error);
      resizeInfo.revert();
      eventBus.emit('globalMessage.show', {
        type: 'error',
        message: t('Erro ao atualizar bloqueio')
      });
    }
  }
}

// Update compromisso via API
async function updateCompromisso(compromisso: Compromisso, newStart: Date | null, newEnd: Date | null): Promise<void> {
  // Extract only the fields required by Agenda_AlterarCompromissoInput
  const updateData: Agenda_AlterarCompromissoInput = {
    compromisso_id: Number(compromisso.compromisso_id),
    compromisso_operador_user_id: String(compromisso['compromisso_operador_user_id'] ?? ''),
    compromisso_orcamento_id: Number(compromisso['compromisso_orcamento_id'] ?? 0),
    compromisso_data_hora_inicio: formatDateTimeForBackend(newStart),
    compromisso_data_hora_fim: formatDateTimeForBackend(newEnd)
  };

  log('AgendaPage', 'updateCompromisso', 'Updating compromisso', updateData);

  await alterarCompromisso(updateData);

  log('AgendaPage', 'updateCompromisso', 'Compromisso updated successfully');

  // Refetch calendar to reload fresh data from backend with all fields
  refreshCalendar();

  eventBus.emit('globalMessage.show', {
    type: 'success',
    message: t('Compromisso atualizado com sucesso')
  });
}

// Update bloqueio via API
async function updateBloqueio(bloqueio: Bloqueio, newStart: Date | null, newEnd: Date | null): Promise<void> {
  const updateData = {
    bloqueio_agenda_id: bloqueio.bloqueio_agenda_id,
    bloqueio_agenda_titulo: bloqueio.bloqueio_agenda_titulo,
    bloqueio_agenda_operador_user_id: bloqueio.bloqueio_agenda_operador_user_id,
    bloqueio_agenda_data_hora_inicio: formatDateTimeForBackend(newStart),
    bloqueio_agenda_data_hora_fim: formatDateTimeForBackend(newEnd)
  };

  log('AgendaPage', 'updateBloqueio', 'Updating bloqueio', updateData);

  await saveBloqueioAgenda(updateData);

  log('AgendaPage', 'updateBloqueio', 'Bloqueio updated successfully');

  // Refetch calendar to reload fresh data from backend
  refreshCalendar();

  eventBus.emit('globalMessage.show', {
    type: 'success',
    message: t('Bloqueio atualizado com sucesso')
  });
}

// Refresh calendar (debounced to prevent race conditions from rapid event emissions)
function refreshCalendar(): void {
  if (refreshDebounceTimer) {
    clearTimeout(refreshDebounceTimer);
    pendingTimers.delete(refreshDebounceTimer);
  }
  refreshDebounceTimer = trackedTimeout(() => {
    log('AgendaPage', 'refreshCalendar', 'Refreshing calendar (debounced)');
    const calendarApi = fullCalendar.value?.getApi();
    if (calendarApi) {
      calendarApi.refetchEvents();
    }
  }, 300);
}

// Handle filter ready event - refresh calendar when filter changes
function handleFilterReady(): void {
  log('AgendaPage', 'handleFilterReady', 'Filter changed, refreshing calendar');
  refreshCalendar();
}

// Wizard handlers - now modal is centralized, just handle completion event
function handleWizardComplete(): void {
  log('AgendaPage', 'handleWizardComplete', 'Wizard completed');
  refreshCalendar();
  eventBus.emit('globalMessage.success', t('Serviço finalizado com sucesso'));
}

// Child component event handlers
const handleBloqueioAgendaOpen = (data: unknown): void => {
  log('AgendaPage', 'handleBloqueioAgendaOpen', 'Opening bloqueio modal via eventBus', data);
  eventBus.emit('modal.agenda-bloqueio.open', data as Record<string, unknown>);
};

// GestaoCompromissoModal handles its own events - no local state needed

// Event listeners
onMounted(() => {
  log('AgendaPage', 'onMounted', 'Component mounted');

  // Initialize mobile detection
  updateMobileDetection();

  log('AgendaPage', 'onMounted', 'Viewport size', {
    width: window.innerWidth,
    height: window.innerHeight,
    isMobile: isMobile.value
  });

  // Calculate initial calendar height before rendering
  nextTick(() => {
    const initialHeight = calculateCalendarHeight();
    calendarHeight.value = initialHeight;
    log('AgendaPage', 'onMounted', 'Initial calendar height set', { height: initialHeight });
  });

  // Add resize listener for mobile detection
  window.addEventListener('resize', handleWindowResize);

  // Sync currentView with FullCalendar's actual view after initial render
  trackedTimeout(() => {
    const api = fullCalendar.value?.getApi();
    if (api?.view?.type) {
      window.__agendaCalendarApi = api;
      currentView.value = api.view.type;
      eventBus.emit('agenda.calendar.ready', { view: api.view.type });
      log('AgendaPage', 'onMounted', 'Synced currentView with calendar', { view: currentView.value });

      // Force scroll to 8am on initial render - use multiple attempts with increasing delays
      if (api.view.type.includes('timeGrid')) {
        // First attempt
        nextTick(() => {
          api.scrollToTime({ hours: 8 });
          log('AgendaPage', 'onMounted', 'Scrolled to 8:00 AM on initial render (attempt 1)');

          // Second attempt after render
          trackedTimeout(() => {
            api.scrollToTime({ hours: 8 });
            log('AgendaPage', 'onMounted', 'Scrolled to 8:00 AM on initial render (attempt 2)');

            // Third attempt to be sure
            trackedTimeout(() => {
              api.scrollToTime({ hours: 8 });
              log('AgendaPage', 'onMounted', 'Scrolled to 8:00 AM on initial render (attempt 3 - final)');
            }, 200);
          }, 100);
        });
      }
    }
  }, 100); // Increased delay to ensure calendar is fully rendered

  // Debug: Add click listener to calendar container to verify clicks are working
  trackedTimeout(() => {
    const calendarEl = document.querySelector('.fc');
    if (calendarEl) {
      log('AgendaPage', 'onMounted', 'Calendar element found, adding debug listeners');
      
      // Test that clicks are being registered
      calendarEl.addEventListener('click', (e) => {
        const me = e as MouseEvent;
        log('AgendaPage', 'calendar-click', 'Calendar clicked', {
          target: (me.target as HTMLElement | null)?.className,
          x: me.clientX,
          y: me.clientY
        });
      });

      // Test touch events on mobile
      calendarEl.addEventListener('touchstart', (e) => {
        const te = e as TouchEvent;
        log('AgendaPage', 'calendar-touchstart', 'Calendar touched', {
          target: (te.target as HTMLElement | null)?.className,
          touches: te.touches.length
        });
      });

      calendarEl.addEventListener('touchend', (e) => {
        const te = e as TouchEvent;
        log('AgendaPage', 'calendar-touchend', 'Calendar touch ended', {
          target: (te.target as HTMLElement | null)?.className
        });
      });
    } else {
      log('AgendaPage', 'onMounted', 'Calendar element NOT found');
    }
  }, 1000);

  // Listen to filter changes - use special handler to skip initial load
  eventBus.on('filter.updated', handleFilterReady);

  // Listen to compromisso events
  eventBus.on('compromisso.saved', refreshCalendar);
  eventBus.on('compromisso.deleted', refreshCalendar);
  eventBus.on('compromisso.created', refreshCalendar);

  // Listen to bloqueio events
  eventBus.on('bloqueio.saved', refreshCalendar);
  eventBus.on('bloqueio.deleted', refreshCalendar);

  // Listen to wizard events (modal handles its own open event in ModalManager.vue)
  eventBus.on('wizard-servicos.completed', handleWizardComplete);

  // Listen to child component open events
  // Note: agenda-add-compromisso.open is handled directly by AgendaAddCompromissoWizard component
  // Note: gestao-compromisso.open is handled by GestaoCompromissoModal in ModalManager.vue
  eventBus.on('bloqueio-agenda.open', handleBloqueioAgendaOpen);
});

onUnmounted(() => {
  log('AgendaPage', 'onUnmounted', 'Component unmounting');

  // Clean up timers
  isUnmounted = true;
  if (refreshDebounceTimer) clearTimeout(refreshDebounceTimer);
  refreshDebounceTimer = null;
  pendingTimers.forEach((timerId) => clearTimeout(timerId));
  pendingTimers.clear();

  // Clean up resize listener
  window.removeEventListener('resize', handleWindowResize);
  delete window.__agendaCalendarApi;

  // Clean up event listeners
  eventBus.off('filter.updated', handleFilterReady);
  eventBus.off('compromisso.saved', refreshCalendar);
  eventBus.off('compromisso.deleted', refreshCalendar);
  eventBus.off('compromisso.created', refreshCalendar);
  eventBus.off('bloqueio.saved', refreshCalendar);
  eventBus.off('bloqueio.deleted', refreshCalendar);
  eventBus.off('wizard-servicos.completed', handleWizardComplete);

  // Clean up child component event listeners
  eventBus.off('bloqueio-agenda.open', handleBloqueioAgendaOpen);
});

// Expose methods for parent/testing
defineExpose({
  refreshCalendar
});
</script>

<style scoped>
.agenda-page {
  width: 100%;
}

.content {
  padding: 20px;
}

/* View Selector with PrimeVue SelectButton */
.view-selector {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  padding: 0;
}

.view-selector :deep(.p-selectbutton) {
  display: flex;
  gap: 0;
}

.view-selector :deep(.p-togglebutton) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  min-height: 44px; /* Better touch target */
}

.view-selector :deep(.p-togglebutton i) {
  font-size: 16px;
}

.view-selector :deep(.p-togglebutton span) {
  white-space: nowrap;
}

.calendar-container {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
}

/* Modern Box styling for calendar */
:deep(.box) {
  position: relative;
}

/* FullCalendar customizations */
:deep(.fc) {
  font-family: inherit;
}

:deep(.fc-time span) {
  font-weight: 900;
  zoom: 1.3;
}

/* Increase time slot height - targets the actual slot divs */
:deep(.fc-timegrid-slot-lane) {
  height: 60px !important;
}

:deep(.fc-timegrid .fc-timegrid-slot) {
  height: 60px !important;
}

:deep(.fc-title) {
  z-index: 3 !important;
}

:deep(.fc-time-grid-event:hover) {
  cursor: pointer;
  opacity: 0.9;
}

:deep(.fc-time-grid-event .fc-content),
:deep(.fc-time-grid-event) {
  overflow: unset !important;
}

:deep(.fc-center h2) {
  margin-top: 20px;
}

:deep(.fc-time) {
  position: relative;
  z-index: 50 !important;
  margin-top: -10px;
}

:deep(.fc-content) {
  z-index: 99 !important;
}

:deep(.event-bloqueio) {
  border: 3px dashed white !important;
  padding: 5px;
}

/* Month view (dayGrid) event styling */
:deep(.fc-daygrid-event) {
  cursor: pointer;
}

:deep(.fc-daygrid-event:hover) {
  opacity: 0.9;
}

:deep(.fc-daygrid-event .fc-event-main) {
  padding: 2px 4px;
}

:deep(.fc-daygrid-event-harness) {
  margin-bottom: 2px;
}

/* Compromisso custom HTML styling */
:deep(.img_cards_container) {
  float: right;
  margin-left: 5px;
  margin-top: -5px;
}

:deep(.img_cards) {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid white;
  object-fit: cover;
}

:deep(.compromissos) {
  position: relative;
  padding: 2px;
  line-height: 1.3;
}

:deep(.compromissos i) {
  vertical-align: middle;
}

/* Tippy.js Custom Theme */
:deep(.tippy-box[data-theme~='agenda']) {
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

:deep(.tippy-box[data-theme~='agenda'][data-placement^='top'] > .tippy-arrow::before) {
  border-top-color: #ffffff;
}

:deep(.tippy-box[data-theme~='agenda'][data-placement^='bottom'] > .tippy-arrow::before) {
  border-bottom-color: #ffffff;
}

:deep(.tippy-box[data-theme~='agenda'][data-placement^='left'] > .tippy-arrow::before) {
  border-left-color: #ffffff;
}

:deep(.tippy-box[data-theme~='agenda'][data-placement^='right'] > .tippy-arrow::before) {
  border-right-color: #ffffff;
}

:deep(.tippy-box[data-theme~='agenda'] > .tippy-content) {
  padding: 0;
}

/* Tooltip Content Styles */
:deep(.tooltip-container) {
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.87);
}

:deep(.tooltip-header) {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px 8px 0 0;
}

:deep(.tooltip-header.tooltip-status-success) {
  color: #2e7d32;
  background-color: rgba(76, 175, 80, 0.08);
}

:deep(.tooltip-header.tooltip-status-default) {
  color: rgba(0, 0, 0, 0.7);
}

:deep(.tooltip-icon) {
  font-size: 16px;
  margin-right: 4px;
}

:deep(.tooltip-divider) {
  height: 1px;
  background-color: rgba(0, 0, 0, 0.08);
  margin: 0;
}

:deep(.tooltip-body) {
  padding: 12px 16px;
}

:deep(.tooltip-row) {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 16px;
}

:deep(.tooltip-row:last-child) {
  margin-bottom: 0;
}

:deep(.tooltip-label) {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  flex-shrink: 0;
}

:deep(.tooltip-value) {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.87);
  text-align: right;
  word-break: break-word;
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.loading-overlay i {
  font-size: 48px;
  color: #667eea;
}

/* Mobile-First Responsive Design */
@media (max-width: 768px) {
  .agenda-page {
    padding: 0;
  }
  
  .content {
    padding: 8px;
  }
  
  /* Mobile view selector */
  .view-selector {
    margin-bottom: 12px;
    padding: 0 8px;
  }
  
  .view-selector :deep(.p-selectbutton) {
    width: 100%;
  }
  
  .view-selector :deep(.p-togglebutton) {
    flex: 1;
    padding: 8px 12px;
    font-size: 13px;
    min-height: 48px; /* Larger touch target on mobile */
  }
  
  .view-selector :deep(.p-togglebutton i) {
    font-size: 14px;
  }
  
  .view-selector :deep(.p-togglebutton span) {
    font-size: 12px;
  }
  
  .calendar-container {
    margin: 0;
    border-radius: 0;
  }
  
  /* Mobile-optimized FullCalendar styles */
  :deep(.fc) {
    font-size: 14px;
    border-radius: 0;
  }
  
  /* Mobile header toolbar */
  :deep(.fc-header-toolbar) {
    padding: 8px 12px;
    margin-bottom: 8px;
  }
  
  :deep(.fc-toolbar-title) {
    font-size: 16px;
    font-weight: 600;
  }
  
  :deep(.fc-button) {
    padding: 6px 8px;
    font-size: 12px;
    border-radius: 6px;
    min-height: 36px;
    touch-action: manipulation;
  }
  
  :deep(.fc-button-group) {
    gap: 4px;
  }
  
  /* Mobile-optimized time grid */
  :deep(.fc-timegrid-slot) {
    min-height: 48px; /* Minimum touch target size */
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.05);
  }
  
  :deep(.fc-timegrid-slot-label) {
    font-size: 12px;
    padding: 4px 8px;
  }
  
  :deep(.fc-timegrid-axis) {
    width: 50px;
  }
  
  /* Mobile-optimized day grid */
  :deep(.fc-daygrid-day) {
    min-height: 60px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.05);
  }
  
  :deep(.fc-daygrid-day-number) {
    font-size: 14px;
    padding: 4px 6px;
  }
  
  :deep(.fc-daygrid-day-top) {
    padding: 2px 4px;
  }
  
  /* Mobile-optimized events */
  :deep(.fc-event) {
    font-size: 12px;
    padding: 2px 4px;
    border-radius: 4px;
    min-height: 24px;
    touch-action: manipulation;
  }
  
  :deep(.fc-event-title) {
    font-size: 11px;
    line-height: 1.2;
  }
  
  :deep(.fc-timegrid-event) {
    margin: 1px 2px;
    border-radius: 4px;
  }
  
  :deep(.fc-daygrid-event) {
    margin: 1px 0;
    border-radius: 4px;
  }
  
  /* Mobile-optimized event content */
  :deep(.compromissos) {
    font-size: 11px;
    line-height: 1.3;
    padding: 2px;
  }
  
  :deep(.img_cards) {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
  
  /* Mobile-optimized more link */
  :deep(.fc-more-link) {
    font-size: 11px;
    padding: 2px 4px;
    border-radius: 4px;
  }
  
  /* Mobile-optimized popover */
  :deep(.fc-popover) {
    max-width: 280px;
    font-size: 12px;
  }
  
  :deep(.fc-popover-header) {
    padding: 8px 12px;
    font-size: 13px;
  }
  
  :deep(.fc-popover-body) {
    padding: 8px 12px;
  }
  
  /* Mobile-optimized now indicator */
  :deep(.fc-timegrid-now-indicator-line) {
    border-width: 2px;
  }
  
  /* Mobile-optimized business hours */
  :deep(.fc-timegrid-bg-harness) {
    opacity: 0.1;
  }
  
  /* Mobile-optimized scrollbars */
  :deep(.fc-scroller) {
    scrollbar-width: thin;
  }
  
  :deep(.fc-scroller::-webkit-scrollbar) {
    width: 4px;
    height: 4px;
  }
  
  :deep(.fc-scroller::-webkit-scrollbar-track) {
    background: #f1f1f1;
  }
  
  :deep(.fc-scroller::-webkit-scrollbar-thumb) {
    background: #c1c1c1;
    border-radius: 2px;
  }
  
  /* Mobile-optimized selection */
  :deep(.fc-highlight) {
    background-color: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.3);
  }
  
  /* Mobile-optimized drag mirror */
  :deep(.fc-event-mirror) {
    opacity: 0.8;
    border-radius: 4px;
  }
  
  /* Mobile-optimized resize handles */
  :deep(.fc-event-resizer) {
    height: 8px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 2px;
  }
  
  /* Mobile-optimized day headers */
  :deep(.fc-col-header-cell) {
    padding: 8px 4px;
    font-size: 12px;
    font-weight: 600;
  }
  
  /* Mobile-optimized week numbers */
  :deep(.fc-daygrid-week-number) {
    font-size: 10px;
    padding: 2px 4px;
  }
  
  /* Mobile-optimized loading overlay */
  .loading-overlay {
    border-radius: 0;
  }
  
  .loading-overlay i {
    font-size: 32px;
  }
}

/* Tablet optimization */
@media (min-width: 769px) and (max-width: 1024px) {
  .content {
    padding: 16px;
  }

  :deep(.fc-button) {
    padding: 8px 12px;
    font-size: 13px;
  }

  :deep(.fc-timegrid-slot) {
    min-height: 40px;
  }

  :deep(.fc-event) {
    font-size: 13px;
    min-height: 20px;
  }
}

/* Desktop optimization */
@media (min-width: 1025px) {
  .content {
    padding: 20px;
  }

  :deep(.fc-button) {
    padding: 10px 16px;
    font-size: 14px;
  }

  :deep(.fc-timegrid-slot) {
    min-height: 30px;
  }

  :deep(.fc-event) {
    font-size: 14px;
    min-height: 18px;
  }
}
</style>
